// Generated by GitHub Copilot
import { useMemo } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import AboutPage from "./pages/AboutPage";
import Chatbot from "./components/Chatbot";
import Footer from "./components/Footer";
import StickyHeader from "./components/StickyHeader";
import HomePage from "./pages/HomePage";
import InsightsPage from "./pages/InsightsPage";
import ProjectsPage from "./pages/ProjectsPage";
import usePortfolioData from "./hooks/usePortfolioData";
import { siteConfig } from "./config/siteConfig";
import projects from "./data/projects.json";
import profileFallback from "./data/profileFallback";

const UPPERCASE_PROJECT_TOKENS = new Set([
  "ai",
  "ml",
  "nlp",
  "llm",
  "api",
  "ui",
  "ux",
  "rpa",
  "ci",
  "cd",
  "aws",
  "gcp",
  "sql",
  "etl",
  "cv",
]);

/**
 * Format an ISO date string into a compact month/year label.
 *
 * @param {string | undefined} value ISO date text.
 * @returns {string} Formatted date label.
 */
function formatMonthYear(value) {
  if (!value) {
    return "Recently updated";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "Recently updated";
  }

  return new Intl.DateTimeFormat("en-US", { month: "short", year: "numeric" }).format(date);
}

/**
 * Convert repository-style headings into readable title case.
 *
 * @param {string | undefined} value Raw heading text.
 * @param {string} [fallback="GitHub Repository"] Fallback heading.
 * @returns {string} Normalized heading text.
 */
function formatProjectHeading(value, fallback = "GitHub Repository") {
  const raw = typeof value === "string" ? value.trim() : "";
  if (!raw) {
    return fallback;
  }

  const normalized = raw.replace(/[-_]+/g, " ").replace(/\s+/g, " ").trim();

  return normalized
    .split(" ")
    .map((segment) => {
      const alphaToken = segment.replace(/[^a-zA-Z]/g, "");
      const lowerAlphaToken = alphaToken.toLowerCase();

      if (alphaToken && UPPERCASE_PROJECT_TOKENS.has(lowerAlphaToken)) {
        return segment.replace(alphaToken, alphaToken.toUpperCase());
      }

      if (/^[A-Z0-9]{2,}$/.test(segment)) {
        return segment;
      }

      return segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase();
    })
    .join(" ");
}

/**
 * Resolve repository description text while avoiding placeholder copy.
 *
 * @param {string | undefined} description Repository description.
 * @param {string | undefined} readmeSummary Repository README summary.
 * @param {string} projectTitle Display title for fallback copy.
 * @returns {string} Display-ready description.
 */
function getProjectDescription(description, readmeSummary, projectTitle) {
  const normalizedDescription = typeof description === "string" ? description.trim() : "";
  const normalizedReadmeSummary = typeof readmeSummary === "string" ? readmeSummary.trim() : "";
  const placeholderSet = new Set([
    "no description provided yet.",
    "no description provided",
    "no description yet.",
    "no description yet",
    "no description.",
    "no description",
  ]);

  if (normalizedDescription && !placeholderSet.has(normalizedDescription.toLowerCase())) {
    return normalizedDescription;
  }

  if (normalizedReadmeSummary && !placeholderSet.has(normalizedReadmeSummary.toLowerCase())) {
    return normalizedReadmeSummary;
  }

  return `Implementation details for ${projectTitle} are available in this repository.`;
}

/**
 * Convert a GitHub repository payload into STAR-friendly project metadata.
 *
 * @param {Record<string, any>} repo Normalized GitHub repository object.
 * @returns {Record<string, any>} Project card object.
 */
function normalizeGithubProject(repo) {
  const tags = Array.isArray(repo?.tags) ? repo.tags.filter(Boolean) : [];
  const updatedLabel = formatMonthYear(repo?.updatedAt);
  const stars = Number.isFinite(repo?.stars) ? repo.stars : 0;
  const forks = Number.isFinite(repo?.forks) ? repo.forks : 0;
  const readmeSummary = typeof repo?.readmeSummary === "string" ? repo.readmeSummary.trim() : "";
  const displayTitle = formatProjectHeading(repo?.title || repo?.name || "GitHub Repository");
  const baseDescription = getProjectDescription(repo?.description, readmeSummary, displayTitle);

  return {
    id: `github-${repo?.id || repo?.title || "repo"}`,
    source: "github",
    title: displayTitle,
    organization: "GitHub Open Source",
    period: `Updated in ${updatedLabel}`,
    description: baseDescription,
    situation:
      readmeSummary
        ? `Repository context from README: ${readmeSummary}`
        : "Open-source projects require maintainable architecture, practical documentation, and reusable implementations for developer adoption.",
    task:
      "Design and maintain production-friendly codebases that demonstrate technical depth and real-world applicability.",
    actions: [
      "Implemented core modules and reusable components to keep development predictable and extensible.",
      "Maintained repository quality through iterative updates, issue-driven refinement, and versioned improvements.",
      "Published source code with documentation to support reproducibility and collaboration.",
    ],
    result: `Repository traction currently includes ${stars} stars and ${forks} forks with ongoing updates through GitHub.`,
    tools: tags.length ? tags : ["GitHub", "Software Engineering"],
    tags: ["GitHub", ...tags].slice(0, 6),
    github: repo?.github || "",
    stars,
    forks,
    updatedAt: repo?.updatedAt || "",
  };
}

function App() {
  const { data: profileData, loading, error } = usePortfolioData();
  const mergedProjects = useMemo(() => {
    const curatedProjects = Array.isArray(projects)
      ? projects.map((project) => ({
        ...project,
        title: formatProjectHeading(project?.title, "Project"),
        source: project.source || "curated",
      }))
      : [];

    const curatedTitleIndex = new Set(
      curatedProjects
        .map((project) => String(project.title || "").trim().toLowerCase())
        .filter(Boolean)
    );

    const liveGithubRepos = Array.isArray(profileData?.github?.repos) ? profileData.github.repos : [];
    const fallbackGithubRepos = Array.isArray(profileFallback?.github?.repos) ? profileFallback.github.repos : [];
    const mergedGithubRepoMap = new Map();

    for (const repo of [...liveGithubRepos, ...fallbackGithubRepos]) {
      const identityKey = String(repo?.github || repo?.id || repo?.title || "").trim().toLowerCase();
      if (!identityKey || mergedGithubRepoMap.has(identityKey)) {
        continue;
      }

      mergedGithubRepoMap.set(identityKey, repo);
    }

    const githubProjects = mergedGithubRepoMap.size
      ? Array.from(mergedGithubRepoMap.values())
        .map(normalizeGithubProject)
        .filter((project) => !curatedTitleIndex.has(String(project.title || "").trim().toLowerCase()))
      : [];

    return [...githubProjects, ...curatedProjects];
  }, [profileData]);

  return (
    <div className="min-h-screen font-body text-[var(--text-primary)]">
      <StickyHeader />

      <main className="mx-auto max-w-6xl px-4 pb-16 pt-4 md:px-8 md:pt-6">
        <Routes>
          <Route
            path="/"
            element={(
              <HomePage
                identityConfig={siteConfig.identity}
                aboutConfig={siteConfig.about}
                contactConfig={siteConfig.contact}
                apiBaseUrl={siteConfig.api.baseUrl}
                profile={profileData}
                loading={loading}
                error={error}
              />
            )}
          />
          <Route
            path="/projects"
            element={<ProjectsPage projects={mergedProjects} config={siteConfig.projects} loading={loading} />}
          />
          <Route path="/about" element={<AboutPage config={siteConfig.about} />} />
          <Route path="/medium" element={<InsightsPage profile={profileData} loading={loading} />} />
          <Route path="/insights" element={<Navigate to="/medium" replace />} />
          <Route path="/contact" element={<Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      {siteConfig.chat.enabled ? (
        <Chatbot config={siteConfig.chat} apiBaseUrl={siteConfig.api.baseUrl} />
      ) : null}

      <Footer />
    </div>
  );
}

export default App;
